import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { GetSessionError } from '~/backend/connector-api/connector-components';
import { SessionPresentation } from '~/backend/connector-api/connector-schemas';
import { APIType } from '~/backend/types';
import { Modality, strToModality } from '~/domain/dicom/modality';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useBackendModels } from '~/hooks/workflows/backend/useBackend';
import useRegistrationTask from '~/hooks/workflows/connector/tasks/useContourPropagationTask';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import ContourPropagation from './ContourPropagation';
import { targetImageFromSession } from './ContourPropagation/registrationTargetImage';
import { sessionContextAsSource } from './ContourPropagation/sessionContextAsSource';
import { useLoadedTargetImages } from './ContourPropagation/useLoadedTargetImages';
import { useTargetImageChoice } from './ContourPropagation/useTargetImageChoice';

type AdaptConnectorWorkflowProps = {
    sessionData: SessionPresentation | undefined;
    isLoadingSession: boolean;
    error: Error | GetSessionError | null;
};

export default function AdaptConnectorWorkflow({
    sessionData,
    isLoadingSession,
    error,
}: AdaptConnectorWorkflowProps) {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const primaryImage = useSelectedPrimaryImage();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const loadedImages = useLoadedTargetImages();
    const { start: startProcessing, isSubmitting } = useRegistrationTask();

    // A session lists the images its launcher offers; without one, the
    // loaded images are the candidates.
    const sessionImages = sessionData?.payload?.images;
    const targetImages = useMemo(
        () =>
            sessionImages
                ? sessionImages.map(targetImageFromSession)
                : loadedImages,
        [sessionImages, loadedImages]
    );

    const [selectedTargetImage, setSelectedTargetImage] =
        useTargetImageChoice(targetImages);

    const { data: availableModels = [], isLoading: isLoadingModels } =
        useBackendModels(APIType.REGISTRATION);

    // The source is the image and structure set the dialog shows, or the
    // session's context where it may stand in for them.
    const onSubmit = (
        selectedModel: PredictionModel,
        selectedStructures: string[],
        selectedCustomization?: string
    ) => {
        const sessionSource = sessionContextAsSource(sessionData, primaryImage);
        startProcessing({
            selectedModel,
            selectedStructures,
            selectedCustomization,
            patientId:
                sessionData?.payload?.patient.id ?? selectedPatientId ?? '',
            sourceImageSeriesUid:
                primaryImage?.seriesUid ??
                sessionSource?.context_image?.series_instance_uid,
            sourceImageStudyUid:
                primaryImage?.studyUid ??
                sessionSource?.context_image?.study_instance_uid,
            sourceStructureSetUid:
                currentStructureSet?.dicomUid ??
                sessionSource?.context_structure_set?.sop_instance_uid,
            targetImageSeriesUid: selectedTargetImage?.seriesId,
            targetImageStudyUid: selectedTargetImage?.studyId,
        });
    };

    // Resolve the source modality, preferring the primary image and falling
    // back to the context image. Unrecognised values become `undefined` so the
    // model-selection hook gets a clean "indeterminate" rather than UNKNOWN.
    const rawSourceModality =
        primaryImage?.seriesMetadata.modality ??
        sessionData?.payload?.context_image?.modality;
    const parsedSourceModality = strToModality(rawSourceModality ?? '');
    const sourceModality =
        parsedSourceModality === Modality.UNKNOWN
            ? undefined
            : parsedSourceModality;

    return (
        <ContourPropagation
            availableModels={availableModels}
            sourceModality={sourceModality}
            onSubmit={onSubmit}
            isLoadingModels={isLoadingModels}
            isLoadingSession={isLoadingSession}
            isSubmitting={isSubmitting}
            targetImages={targetImages}
            selectedTargetImage={selectedTargetImage}
            setSelectedTargetImage={setSelectedTargetImage}
            error={error}
            showPseudonymization={false}
        />
    );
}
